import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Card, Row, Col, Badge, Spinner, Alert, Container, Button, Form, InputGroup, Collapse } from 'react-bootstrap';
import {
    FaCalendarAlt,
    FaCheckCircle,
    FaClock,
    FaChartLine,
    FaTasks,
    FaSync,
    FaBookOpen,
    FaFolderOpen,
    FaFileAlt,
    FaChevronDown,
    FaChevronUp,
    FaCheck,
    FaClipboardList,
    FaGraduationCap,
    FaTimesCircle
} from 'react-icons/fa';
import moment from 'moment';
import Header from './Header';
import { ProductContext } from '../context/AppContext';

const PAGE_SIZE = 6; // Number of chapters per load
const RESULT_PAGE_SIZE = 3; // Number of test results per load

// Helper function to extract and map 3 types of results & testMaps details for a test according to Scenario 1-4 rules
const getTestDetails = (tItem) => {
    const isPublished = Boolean(tItem.is_publish);
    const results = isPublished && Array.isArray(tItem.results) ? tItem.results : [];
    const testMaps = Array.isArray(tItem.testMaps) ? tItem.testMaps : [];

    // 1. Overall Result from API (paper_id: null, chapter_id: null)
    const overallResult = results.find(
        (r) => (r.paper_id === null || r.paper_id === undefined) && (r.chapter_id === null || r.chapter_id === undefined)
    );

    const overallEnteredMarks = (overallResult && overallResult.marks_obtained !== null && overallResult.marks_obtained !== undefined)
        ? Number(overallResult.marks_obtained)
        : null;

    // 2 & 3. Group papers & chapters
    const paperMap = new Map();

    testMaps.forEach((map) => {
        const pId = map.paper_id;
        if (!pId) return;

        if (!paperMap.has(pId)) {
            paperMap.set(pId, {
                paperId: pId,
                paperName: map.paper?.name || `Paper ${pId}`,
                paperObj: map.paper,
                paperMapItem: null,
                paperResult: null,
                chapters: []
            });
        }

        const pEntry = paperMap.get(pId);
        if (map.paper?.name && (!pEntry.paperName || pEntry.paperName.startsWith('Paper '))) {
            pEntry.paperName = map.paper.name;
        }

        if (!map.chapter_id && !map.chapter) {
            pEntry.paperMapItem = map;
        } else {
            const chId = map.chapter_id || map.chapter?.id;
            const chResult = results.find((r) => Number(r.paper_id) === Number(pId) && Number(r.chapter_id) === Number(chId));
            const chMarks = (chResult && chResult.marks_obtained !== null && chResult.marks_obtained !== undefined)
                ? Number(chResult.marks_obtained)
                : null;

            pEntry.chapters.push({
                mapId: map.id,
                chapterId: chId,
                chapterName: map.chapter?.name || `Chapter ${chId}`,
                chapterDescription: map.chapter?.description || '',
                maxMarks: map.max_marks !== null && map.max_marks !== undefined ? Number(map.max_marks) : null,
                minMarks: map.min_marks !== null && map.min_marks !== undefined ? Number(map.min_marks) : null,
                result: chResult,
                marksObtained: chMarks
            });
        }
    });

    // Attach paper-level results (Type 2 result)
    results.forEach((r) => {
        if (r.paper_id) {
            const pId = Number(r.paper_id);
            if (!paperMap.has(pId)) {
                paperMap.set(pId, {
                    paperId: pId,
                    paperName: `Paper ${pId}`,
                    paperObj: null,
                    paperMapItem: null,
                    paperResult: null,
                    chapters: []
                });
            }
            const pEntry = paperMap.get(pId);
            if ((!r.chapter_id || r.chapter_id === null) && !pEntry.paperResult) {
                pEntry.paperResult = r;
            }
        }
    });

    // Compute paper-level final marks according to Scenario 1-4
    let sumOfPaperMarksForOverall = 0;
    let hasAnyPaperMarksForOverall = false;

    const paperList = Array.from(paperMap.values()).map((pEntry) => {
        const pResult = pEntry.paperResult;
        const pEnteredMarks = (pResult && pResult.marks_obtained !== null && pResult.marks_obtained !== undefined)
            ? Number(pResult.marks_obtained)
            : null;

        let finalPaperMarks = null;
        let isPaperCalculated = false;

        if (pEnteredMarks !== null) {
            // Scenario 1 & 2: Paper marks entered in API -> Show entered
            finalPaperMarks = pEnteredMarks;
            isPaperCalculated = false;
        } else {
            // Scenario 3 & 4: Paper marks NOT entered -> Sum chapter marks for that paper
            let chapterSum = 0;
            let hasChapterMarks = false;
            pEntry.chapters.forEach((ch) => {
                if (ch.marksObtained !== null) {
                    chapterSum += Number(ch.marksObtained);
                    hasChapterMarks = true;
                }
            });
            if (hasChapterMarks) {
                finalPaperMarks = chapterSum;
                isPaperCalculated = true;
            }
        }

        if (finalPaperMarks !== null) {
            sumOfPaperMarksForOverall += finalPaperMarks;
            hasAnyPaperMarksForOverall = true;
        }

        // Calculate paper max marks if not explicitly present
        let paperMax = pEntry.paperMapItem?.max_marks;
        if ((paperMax === null || paperMax === undefined) && pEntry.chapters.length > 0) {
            let chMaxSum = 0;
            let hasChMax = false;
            pEntry.chapters.forEach(ch => {
                if (ch.maxMarks !== null) {
                    chMaxSum += ch.maxMarks;
                    hasChMax = true;
                }
            });
            if (hasChMax) paperMax = chMaxSum;
        }

        return {
            ...pEntry,
            paperMarksObtained: pEnteredMarks,
            finalPaperMarks,
            isPaperCalculated,
            paperMaxMarks: paperMax
        };
    });

    // Compute Overall Final Marks according to Scenario 1-4
    let finalOverallMarks = null;
    let isOverallCalculated = false;

    if (overallEnteredMarks !== null) {
        // Scenario 1 & 3: Overall marks entered in API -> Show entered
        finalOverallMarks = overallEnteredMarks;
        isOverallCalculated = false;
    } else if (hasAnyPaperMarksForOverall) {
        // Scenario 2 & 4: Overall marks NOT entered -> Add paper marks (entered or calculated from chapters)
        finalOverallMarks = sumOfPaperMarksForOverall;
        isOverallCalculated = true;
    }

    return {
        overallMaxMarks: tItem.max_marks,
        overallMinMarks: tItem.min_marks,
        overallResult: overallResult,
        overallEnteredMarks,
        overallMarksObtained: finalOverallMarks,
        isOverallCalculated,
        papers: paperList
    };
};

const ChapterTest = () => {
    const { clsSecId: contextClsSecId, GetstudentData } = useContext(ProductContext);
    const clsSecId = contextClsSecId || GetstudentData?.cls_sec?.id || GetstudentData?.class_section_id;
    const studentId = GetstudentData?.id;
    const authtoken = localStorage.getItem("token");

    // Active Tab: 'track' or 'result'
    const [activeTab, setActiveTab] = useState('track');

    // Data States
    const [rawSubjectPapers, setRawSubjectPapers] = useState([]);
    const [loadingSubjectPapers, setLoadingSubjectPapers] = useState(false);
    const [errorSubjectPapers, setErrorSubjectPapers] = useState(null);

    const [selectedSubjectId, setSelectedSubjectId] = useState(null);
    const [selectedPaperId, setSelectedPaperId] = useState(null);

    // Chapters list for selected paper
    const [chapters, setChapters] = useState([]);
    const [loadingChapters, setLoadingChapters] = useState(false);
    const [errorChapters, setErrorChapters] = useState(null);

    // Pagination: Load More state
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
    const [visibleResultCount, setVisibleResultCount] = useState(RESULT_PAGE_SIZE);

    // Tests by Subject states (for Chapter Result tab)
    const [subjectTests, setSubjectTests] = useState([]);
    const [loadingSubjectTests, setLoadingSubjectTests] = useState(false);
    const [errorSubjectTests, setErrorSubjectTests] = useState(null);

    // Collapse states for Chapter Result tab (default collapsed)
    const [openTests, setOpenTests] = useState({});
    const [openPapers, setOpenPapers] = useState({});
    const [resultPublishFilter, setResultPublishFilter] = useState('all'); // 'all', 'published', 'unpublished'

    const toggleTestOpen = (testId) => {
        setOpenTests((prev) => ({
            ...prev,
            [testId]: prev[testId] === true ? false : true
        }));
    };

    const togglePaperOpen = (key) => {
        setOpenPapers((prev) => ({
            ...prev,
            [key]: prev[key] === true ? false : true
        }));
    };

    // Filter & Search states
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'completed', 'pending'

    // Subject Icon & Stripe styling matching Homework.js & Notice.js
    const getSubjectStyle = (subjectName = "") => {
        const sub = subjectName.toLowerCase();
        if (sub.includes("math")) {
            return {
                stripeColor: "#818cf8", // Indigo
                bgColor: "#f5f3ff",
                textColor: "#6d28d9",
                iconText: "√x"
            };
        }
        if (sub.includes("english") || sub.includes("lang")) {
            return {
                stripeColor: "#34d399", // Emerald Green
                bgColor: "#ecfdf5",
                textColor: "#047857",
                iconText: "ENG"
            };
        }
        if (sub.includes("science") || sub.includes("evs") || sub.includes("bio") || sub.includes("chem") || sub.includes("phy")) {
            return {
                stripeColor: "#60a5fa", // Blue
                bgColor: "#eff6ff",
                textColor: "#1d4ed8",
                iconText: "SCI"
            };
        }
        if (sub.includes("hindi")) {
            return {
                stripeColor: "#f43f5e", // Rose
                bgColor: "#fff1f2",
                textColor: "#be123c",
                iconText: "HIN"
            };
        }
        if (sub.includes("gk") || sub.includes("general")) {
            return {
                stripeColor: "#fb923c", // Orange
                bgColor: "#fff7ed",
                textColor: "#c2410c",
                iconText: "GK"
            };
        }
        return {
            stripeColor: "#a78bfa", // Purple/slate
            bgColor: "#f8fafc",
            textColor: "#475569",
            iconText: "CH"
        };
    };

    // Fetch Subject Papers List
    const fetchSubjectPapers = async () => {
        if (!clsSecId) return;
        try {
            setLoadingSubjectPapers(true);
            setErrorSubjectPapers(null);
            const res = await fetch(
                `https://api.mirai.healthcare/api/masters/subject-paper?cls_sec_id=${clsSecId}`,
                {
                    method: "GET",
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                        "x-auth": authtoken,
                    },
                }
            );

            const result = await res.json();
            if (result.success && Array.isArray(result.data)) {
                setRawSubjectPapers(result.data);
            } else {
                setRawSubjectPapers([]);
            }
        } catch (err) {
            console.error("Error fetching subject papers", err);
            setErrorSubjectPapers("Failed to load subjects. Please try again.");
        } finally {
            setLoadingSubjectPapers(false);
        }
    };

    useEffect(() => {
        fetchSubjectPapers();
    }, [clsSecId]);

    // Group items into unique subjects with papers
    const subjects = useMemo(() => {
        const subjectMap = new Map();
        rawSubjectPapers.forEach((item) => {
            if (item?.subject && !item.subject.is_deleted) {
                const subId = item.subject.id;
                if (!subjectMap.has(subId)) {
                    subjectMap.set(subId, {
                        ...item.subject,
                        papers: []
                    });
                }
                if (item?.paper && !item.paper.is_deleted) {
                    const existingPapers = subjectMap.get(subId).papers;
                    const pId = item.paper.id || item.paper_id;
                    if (!existingPapers.some(p => p.id === pId)) {
                        existingPapers.push({
                            ...item.paper,
                            id: pId,
                            subjectPaperId: item.id,
                            is_optional: item.is_optional
                        });
                    }
                }
            }
        });
        return Array.from(subjectMap.values());
    }, [rawSubjectPapers]);

    // Select default subject & paper on load
    useEffect(() => {
        if (subjects.length > 0 && !selectedSubjectId) {
            const firstSub = subjects[0];
            setSelectedSubjectId(firstSub.id);
            if (firstSub.papers && firstSub.papers.length > 0) {
                const firstPaperId = firstSub.papers[0].id;
                setSelectedPaperId(firstPaperId);
            }
        }
    }, [subjects, selectedSubjectId]);

    const currentSubject = useMemo(() => {
        return subjects.find(s => s.id === selectedSubjectId) || null;
    }, [subjects, selectedSubjectId]);

    const papers = useMemo(() => {
        return currentSubject ? currentSubject.papers : [];
    }, [currentSubject]);

    const currentPaper = useMemo(() => {
        return papers.find(p => p.id === selectedPaperId) || null;
    }, [papers, selectedPaperId]);

    // Fetch Tests by Subject (For Chapter Result Tab)
    const fetchSubjectTests = async (subId) => {
        if (!clsSecId || !subId) return;
        try {
            setLoadingSubjectTests(true);
            const sessionId = GetstudentData?.session || GetstudentData?.session_id || 1;
            const studentQuery = studentId ? `&student_id=${studentId}` : '';
            const res = await fetch(
                `https://api.mirai.healthcare/api/paper/chapter-test/by-subject?session_id=${sessionId}&include_results=true&cls_sec_id=${clsSecId}&subject_id=${subId}${studentQuery}`,
                {
                    method: "GET",
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                        "x-auth": authtoken,
                    },
                }
            );

            const result = await res.json();
            const testList = Array.isArray(result?.message)
                ? result.message
                : (Array.isArray(result?.data) ? result.data : []);

            setSubjectTests(testList);
        } catch (err) {
            console.error("Error fetching subject tests", err);
            setErrorSubjectTests("Failed to load tests for this subject.");
        } finally {
            setLoadingSubjectTests(false);
        }
    };

    const handleSelectSubject = (subId) => {
        setSelectedSubjectId(subId);
        setVisibleCount(PAGE_SIZE);
        setVisibleResultCount(RESULT_PAGE_SIZE);
        const sub = subjects.find(s => s.id === subId);
        if (sub && sub.papers && sub.papers.length > 0) {
            const firstPId = sub.papers[0].id;
            setSelectedPaperId(firstPId);
            fetchChapters(firstPId);
        } else {
            setSelectedPaperId(null);
            setChapters([]);
        }

        if (activeTab === 'result') {
            fetchSubjectTests(subId);
        }
    };

    const handleSelectPaper = (paperId) => {
        setSelectedPaperId(paperId);
        setVisibleCount(PAGE_SIZE);
        setChapters([]);
        fetchChapters(paperId);
    };

    // Fetch Chapters for selected paper
    const fetchChapters = async (paperId) => {
        if (!clsSecId || !paperId) return;
        try {
            setLoadingChapters(true);
            setErrorChapters(null);
            const res = await fetch(
                `https://api.mirai.healthcare/api/paper/chapter?cls_sec_id=${clsSecId}&paper_id=${paperId}`,
                {
                    method: "GET",
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                        "x-auth": authtoken,
                    },
                }
            );

            const result = await res.json();
            const chapterList = Array.isArray(result?.message)
                ? result.message
                : (Array.isArray(result?.data) ? result.data : []);

            // Sort chapters by name (natural order, so Chapter 1, 2, ... 10 instead of alphabetical)
            const sortedChapters = [...chapterList].sort((a, b) => {
                const nameA = (a.chapter || a).name || "";
                const nameB = (b.chapter || b).name || "";
                return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: "base" });
            });

            setChapters(sortedChapters);
        } catch (err) {
            console.error("Error fetching chapters", err);
            setErrorChapters("Failed to load chapters for this paper.");
        } finally {
            setLoadingChapters(false);
        }
    };

    useEffect(() => {
        if (selectedPaperId && clsSecId && activeTab === 'track') {
            fetchChapters(selectedPaperId);
        }
    }, [clsSecId, selectedPaperId, activeTab]);

    useEffect(() => {
        if (activeTab === 'result' && selectedSubjectId && clsSecId) {
            fetchSubjectTests(selectedSubjectId);
        }
    }, [activeTab, selectedSubjectId, clsSecId]);

    // Reset pagination on filter or search change
    useEffect(() => {
        setVisibleCount(PAGE_SIZE);
        setVisibleResultCount(RESULT_PAGE_SIZE);
    }, [searchQuery, statusFilter, activeTab]);

    // Filter chapters
    const filteredChapters = useMemo(() => {
        return chapters.filter((ch) => {
            const chapterObj = ch.chapter || ch;
            const nameMatch = (chapterObj.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                (chapterObj.description || '').toLowerCase().includes(searchQuery.toLowerCase());

            const isCompleted = Boolean(chapterObj.complete_date);

            if (statusFilter === 'completed') return nameMatch && isCompleted;
            if (statusFilter === 'pending') return nameMatch && !isCompleted;
            return nameMatch;
        });
    }, [chapters, searchQuery, statusFilter]);

    // Calculate Completion Progress stats
    const totalChaptersCount = chapters.length;
    const completedChaptersCount = useMemo(() => {
        return chapters.filter(c => Boolean((c.chapter || c).complete_date)).length;
    }, [chapters]);

    // Chapters slice for Load More
    const visibleChapters = useMemo(() => {
        return filteredChapters.slice(0, visibleCount);
    }, [filteredChapters, visibleCount]);

    const handleLoadMore = () => {
        setVisibleCount((prev) => prev + PAGE_SIZE);
    };

    const capitalize = (str = '') => str ? str.charAt(0).toUpperCase() + str.slice(1) : '';
    const subjectStyle = getSubjectStyle(currentSubject?.name || '');

    return (
        <div className="page-wrapper bg-light min-vh-100">
            <Header />

            <Container fluid className="px-2 px-md-3 py-3 pb-5 mb-5" style={{ maxWidth: '1000px' }}>

                {/* Tab Switcher & Refresh Button Row */}
                <div className="d-flex align-items-center justify-content-between border-bottom mb-3 bg-white rounded-3 px-3 pt-2 shadow-sm">
                    <div className="d-flex">
                        <button
                            className={`btn tab-simple-btn py-2 px-3 px-sm-4 fw-bold me-2 border-0 rounded-0 ${activeTab === 'track' ? 'tab-simple-active' : 'text-secondary'
                                }`}
                            onClick={() => setActiveTab('track')}
                        >
                            <FaTasks className="me-2" /> 1. Chapter Track
                        </button>
                        <button
                            className={`btn tab-simple-btn py-2 px-3 px-sm-4 fw-bold border-0 rounded-0 ${activeTab === 'result' ? 'tab-simple-active' : 'text-secondary'
                                }`}
                            onClick={() => setActiveTab('result')}
                        >
                            <FaChartLine className="me-2" /> 2. Chapter Result
                        </button>
                    </div>

                    <Button
                        variant="light"
                        size="sm"
                        className="border text-secondary rounded-3 d-flex align-items-center gap-2 px-3 py-1 mb-1"
                        onClick={() => {
                            fetchSubjectPapers();
                            if (activeTab === 'track' && selectedPaperId) fetchChapters(selectedPaperId);
                            if (activeTab === 'result' && selectedSubjectId) fetchSubjectTests(selectedSubjectId);
                        }}
                    >
                        <FaSync className={loadingSubjectPapers || loadingChapters || loadingSubjectTests ? 'spin' : ''} />
                        <span className="d-none d-sm-inline">Refresh</span>
                    </Button>
                </div>

                {loadingSubjectPapers ? (
                    <div className="text-center py-5 bg-white rounded-3 border shadow-sm">
                        <Spinner animation="border" size="sm" variant="primary" />
                        <span className="ms-2 text-muted small">Loading subjects...</span>
                    </div>
                ) : errorSubjectPapers ? (
                    <Alert variant="danger" className="rounded-3 border-0 py-2">
                        {errorSubjectPapers}
                    </Alert>
                ) : subjects.length === 0 ? (
                    <div className="text-center py-5 bg-white rounded-3 border">
                        <FaFolderOpen className="fs-3 text-muted mb-1 opacity-50" />
                        <h6 className="fw-bold text-dark mb-0">No Subjects Available</h6>
                    </div>
                ) : (
                    <div>
                        {/* STEP 1: SUBJECT SELECTOR */}
                        <div className="bg-white p-3 rounded-3 border mb-3 shadow-sm">
                            <div className="fw-bold text-dark mb-2 fs-8 text-uppercase text-muted d-flex align-items-center gap-2">
                                <FaBookOpen className="text-primary" /> Step 1: Select Subject
                            </div>
                            <div className="d-flex flex-wrap gap-2">
                                {subjects.map((sub) => {
                                    const isSelected = sub.id === selectedSubjectId;
                                    return (
                                        <button
                                            key={sub.id}
                                            onClick={() => handleSelectSubject(sub.id)}
                                            className={`btn btn-sm rounded-3 py-1 px-3 fw-bold transition-all ${isSelected
                                                    ? 'btn-primary shadow-sm'
                                                    : 'btn-light border text-dark'
                                                }`}
                                        >
                                            <span className="text-capitalize">{sub.name}</span>
                                            <span className={`badge ms-2 ${isSelected ? 'bg-white text-primary' : 'bg-secondary-subtle text-secondary'}`}>
                                                {sub.papers.length}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* TAB 1: CHAPTER TRACK */}
                        {activeTab === 'track' && (
                            <div>
                                {/* STEP 2: PAPER SELECTOR */}
                                {currentSubject && (
                                    <div className="bg-white p-3 rounded-3 border mb-3 shadow-sm">
                                        <div className="fw-bold text-dark mb-2 fs-8 text-uppercase text-muted d-flex align-items-center gap-2">
                                            <FaFileAlt className="text-primary" /> Step 2: Select Paper ({currentSubject.name})
                                        </div>
                                        <div className="d-flex flex-wrap gap-2">
                                            {papers.map((paper) => {
                                                const isSelected = paper.id === selectedPaperId;
                                                return (
                                                    <button
                                                        key={paper.id}
                                                        onClick={() => handleSelectPaper(paper.id)}
                                                        className={`btn btn-sm rounded-3 py-1 px-3 fw-semibold transition-all ${isSelected
                                                                ? 'btn-dark shadow-sm'
                                                                : 'btn-outline-secondary'
                                                            }`}
                                                    >
                                                        <span className="text-capitalize">{paper.name}</span>
                                                        {paper.is_optional && <span className="ms-1 fs-8 text-muted">(Optional)</span>}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                <Card className="border-0 shadow-none bg-transparent">
                                    <Card.Header className="bg-white p-3 rounded-3 border mb-3 d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-2 shadow-sm">
                                        <h6 className="fw-bold text-dark mb-0">
                                            Chapter List
                                            {currentPaper && <span className="text-primary font-normal ms-2">({capitalize(currentPaper.name)})</span>}
                                        </h6>

                                        {/* Search & Status Filters */}
                                        <div className="d-flex flex-wrap align-items-center gap-2">
                                            <div className="btn-group btn-group-sm" role="group">
                                                <button
                                                    className={`btn ${statusFilter === 'all' ? 'btn-primary' : 'btn-light border'}`}
                                                    onClick={() => setStatusFilter('all')}
                                                >
                                                    All ({chapters.length})
                                                </button>
                                                <button
                                                    className={`btn ${statusFilter === 'completed' ? 'btn-success' : 'btn-light border'}`}
                                                    onClick={() => setStatusFilter('completed')}
                                                >
                                                    Completed ({completedChaptersCount})
                                                </button>
                                                <button
                                                    className={`btn ${statusFilter === 'pending' ? 'btn-warning' : 'btn-light border'}`}
                                                    onClick={() => setStatusFilter('pending')}
                                                >
                                                    Pending ({totalChaptersCount - completedChaptersCount})
                                                </button>
                                            </div>

                                            <InputGroup size="sm" style={{ width: '180px' }}>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Search chapter..."
                                                    value={searchQuery}
                                                    onChange={(e) => setSearchQuery(e.target.value)}
                                                />
                                            </InputGroup>
                                        </div>
                                    </Card.Header>

                                    <Card.Body className="p-0">
                                        {loadingChapters ? (
                                            <div className="text-center py-5 bg-white rounded-3 border shadow-sm">
                                                <Spinner animation="border" size="sm" variant="primary" />
                                                <p className="mt-2 text-muted small mb-0">Loading chapters...</p>
                                            </div>
                                        ) : errorChapters ? (
                                            <Alert variant="warning" className="rounded-3 border-0 py-2">
                                                {errorChapters}
                                            </Alert>
                                        ) : filteredChapters.length === 0 ? (
                                            <div className="text-center py-5 bg-white rounded-3 border shadow-sm">
                                                <p className="text-muted mb-0 small">No chapters found for this selection.</p>
                                            </div>
                                        ) : (
                                            <div>
                                                {/* Vertical Progress Timeline matching SchoolNotice.js */}
                                                <div className="timeline-container position-relative">
                                                    <div className="timeline-line" />

                                                    {visibleChapters.map((item, index) => {
                                                        const ch = item.chapter || item;
                                                        const isCompleted = Boolean(ch.complete_date);

                                                        const statusBadgeStyle = isCompleted
                                                            ? { backgroundColor: '#e0fdf4', color: '#166534', border: '1px solid #bbf7d0' }
                                                            : { backgroundColor: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' };

                                                        return (
                                                            <div className="timeline-item position-relative mb-3" key={ch.id || index}>
                                                                {/* Timeline Dot Circle in front of Chapter (SchoolNotice style) */}
                                                                <div
                                                                    className={`timeline-dot rounded-circle ${isCompleted ? 'bg-success text-white' : 'bg-primary text-white'}`}
                                                                    style={{
                                                                        backgroundColor: isCompleted ? '#10b981' : subjectStyle.stripeColor
                                                                    }}
                                                                >
                                                                    {isCompleted ? <FaCheck className="fs-12" /> : (index + 1)}
                                                                </div>

                                                                {/* Chapter Card (Green when completed) */}
                                                                <div
                                                                    className="timeline-content p-3 d-flex flex-column justify-content-between w-100 transition-all"
                                                                    style={{
                                                                        backgroundColor: isCompleted ? '#f0fdf4' : '#ffffff',
                                                                        border: isCompleted ? '1px solid #bbf7d0' : '1px solid #f1f5f9',
                                                                        borderRadius: '14px',
                                                                        borderLeft: `6px solid ${isCompleted ? '#10b981' : subjectStyle.stripeColor}`,
                                                                        boxShadow: isCompleted ? '0 2px 6px rgba(16, 185, 129, 0.08)' : '0 1px 3px 0 rgba(0,0,0,0.03)',
                                                                    }}
                                                                >
                                                                    {/* Upper Section: Title & Description */}
                                                                    <div className="d-flex align-items-start gap-3 w-100 mb-2">
                                                                        <div className="flex-grow-1">
                                                                            <h5 className="fw-bold mb-1" style={{ fontSize: '15px', color: isCompleted ? '#065f46' : '#1e293b' }}>
                                                                                {capitalize(ch.name)}
                                                                            </h5>
                                                                            <span className="fw-semibold d-block mb-1" style={{ color: isCompleted ? '#047857' : subjectStyle.textColor, fontSize: '13px' }}>
                                                                                {currentSubject?.name ? capitalize(currentSubject.name) : 'Chapter'}
                                                                            </span>
                                                                            {ch.description ? (
                                                                                <p className="text-muted fs-12 mb-0 line-clamp-2" style={{ lineHeight: '1.4' }}>
                                                                                    {ch.description}
                                                                                </p>
                                                                            ) : (
                                                                                <p className="text-muted fs-12 mb-0 italic" style={{ lineHeight: '1.4' }}>
                                                                                    No description provided.
                                                                                </p>
                                                                            )}
                                                                        </div>
                                                                    </div>

                                                                    {/* Lower Section: Deadline Date & Completed/Pending Status Badge */}
                                                                    <div className="pt-2 border-top w-100 d-flex flex-wrap align-items-center justify-content-between gap-2" style={{ borderColor: isCompleted ? '#d1fae5' : '#f8fafc' }}>
                                                                        <div className="d-flex align-items-center gap-3 text-secondary" style={{ fontSize: '11px' }}>
                                                                            <span className="d-inline-flex align-items-center gap-1">
                                                                                <FaCalendarAlt className="text-primary fs-12" />
                                                                                Deadline: {ch.deadline_date ? moment(ch.deadline_date).format("DD MMM YYYY") : "N/A"}
                                                                            </span>
                                                                        </div>

                                                                        <div>
                                                                            <span className="badge px-2.5 py-1.5 fs-11 fw-semibold d-inline-flex align-items-center gap-1" style={{ ...statusBadgeStyle, borderRadius: '6px' }}>
                                                                                {isCompleted ? (
                                                                                    <>
                                                                                        <FaCheckCircle className="fs-12 text-success" />
                                                                                        Completed on {moment(ch.complete_date).format("DD MMM YYYY")}
                                                                                    </>
                                                                                ) : (
                                                                                    <>
                                                                                        <FaClock className="fs-12" />
                                                                                        Pending
                                                                                    </>
                                                                                )}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>

                                                {/* LOAD MORE BUTTON FOR CHAPTER TRACK */}
                                                {filteredChapters.length > visibleCount && (
                                                    <div className="text-center my-4 py-2">
                                                        <Button
                                                            variant="primary"
                                                            className="rounded-pill px-4 py-2.5 fw-bold shadow-sm d-inline-flex align-items-center gap-2"
                                                            onClick={handleLoadMore}
                                                        >
                                                            <span>Load More Chapters ({filteredChapters.length - visibleCount} remaining)</span>
                                                            <FaChevronDown className="fs-8" />
                                                        </Button>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                            </div>
                        )}

                        {/* TAB 2: CHAPTER RESULT (BY SUBJECT API) */}
                        {activeTab === 'result' && (
                            <div>
                                {loadingSubjectTests ? (
                                    <div className="text-center py-5 bg-white rounded-4 border shadow-sm">
                                        <Spinner animation="border" size="sm" variant="primary" />
                                        <p className="mt-2 text-muted small mb-0 fw-medium">Loading test results for {currentSubject?.name || 'selected subject'}...</p>
                                    </div>
                                ) : errorSubjectTests ? (
                                    <Alert variant="warning" className="rounded-3 border-0 py-3 shadow-sm">
                                        {errorSubjectTests}
                                    </Alert>
                                ) : subjectTests.length === 0 ? (
                                    <div className="text-center py-5 bg-white rounded-4 border shadow-sm">
                                        <div className="avatar avatar-lg bg-light rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center" style={{ width: '60px', height: '60px' }}>
                                            <FaClipboardList className="fs-3 text-muted opacity-50" />
                                        </div>
                                        <h6 className="fw-bold text-dark mb-1">No Test Results Found</h6>
                                        <p className="text-muted small mb-0">No chapter tests available for {currentSubject?.name || 'this subject'}.</p>
                                    </div>
                                ) : (
                                    <div className="d-flex flex-column gap-4">
                                        {/* Subject Result Header Summary & Filters */}
                                        {(() => {
                                            const publishedCount = subjectTests.filter(t => Boolean(t.is_publish)).length;
                                            const unpublishedCount = subjectTests.length - publishedCount;

                                            const filteredSubjectTests = subjectTests.filter((tItem) => {
                                                const isPublished = Boolean(tItem.is_publish);
                                                const matchesSearch = searchQuery
                                                    ? (tItem.name || '').toLowerCase().includes(searchQuery.toLowerCase())
                                                    : true;

                                                if (resultPublishFilter === 'published') return matchesSearch && isPublished;
                                                if (resultPublishFilter === 'unpublished') return matchesSearch && !isPublished;
                                                return matchesSearch;
                                            });

                                            return (
                                                <>
                                                    <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between bg-white p-3 rounded-3 border shadow-xs gap-3">
                                                        <div className="d-flex align-items-center gap-2">
                                                            <span className="badge px-3 py-2 fs-12 fw-bold text-uppercase" style={{ backgroundColor: subjectStyle.bgColor, color: subjectStyle.textColor }}>
                                                                {currentSubject?.name || 'Subject'}
                                                            </span>
                                                            <span className="text-secondary fs-13 fw-medium">
                                                                Test results & chapter mark evaluations
                                                            </span>
                                                        </div>

                                                        {/* Is Publish Filter Buttons */}
                                                        <div className="d-flex flex-wrap align-items-center gap-2">
                                                            <div className="btn-group btn-group-sm" role="group">
                                                                <button
                                                                    className={`btn ${resultPublishFilter === 'all' ? 'btn-primary' : 'btn-light border'}`}
                                                                    onClick={() => setResultPublishFilter('all')}
                                                                >
                                                                    All ({subjectTests.length})
                                                                </button>
                                                                <button
                                                                    className={`btn ${resultPublishFilter === 'published' ? 'btn-success' : 'btn-light border'}`}
                                                                    onClick={() => setResultPublishFilter('published')}
                                                                >
                                                                    Published ({publishedCount})
                                                                </button>
                                                                <button
                                                                    className={`btn ${resultPublishFilter === 'unpublished' ? 'btn-warning' : 'btn-light border'}`}
                                                                    onClick={() => setResultPublishFilter('unpublished')}
                                                                >
                                                                    Unpublished ({unpublishedCount})
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {filteredSubjectTests.length === 0 ? (
                                                        <div className="text-center py-5 bg-white rounded-4 border shadow-sm">
                                                            <p className="text-muted mb-0 small">No test results match the selected filter.</p>
                                                        </div>
                                                    ) : (
                                                        filteredSubjectTests.slice(0, visibleResultCount).map((tItem, tIdx) => {
                                                            const testInfo = getTestDetails(tItem);
                                                            const { overallMaxMarks, overallMinMarks, overallMarksObtained, isOverallCalculated, papers } = testInfo;

                                                            const isPublished = Boolean(tItem.is_publish);
                                                            const isOverallPassed = overallMarksObtained !== null && overallMinMarks !== null && overallMarksObtained >= overallMinMarks;
                                                            const overallPercentage = overallMarksObtained !== null && overallMaxMarks ? Math.round((overallMarksObtained / overallMaxMarks) * 100) : null;

                                                            return (
                                                                <Card
                                                                    key={tItem.id || tIdx}
                                                                    className="border-0 shadow-sm rounded-4 bg-white overflow-hidden transition-all chapter-result-card"
                                                                    style={{ borderLeft: `6px solid ${isPublished ? '#10b981' : '#f59e0b'}` }}
                                                                >
                                                                    {/* Test Header with Green Published Status Badge & Hero Score Widget */}
                                                                    <Card.Header className="bg-white p-3 p-md-4 border-bottom">
                                                                        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                                                                            <div>
                                                                                <div className="d-flex align-items-center gap-2 mb-1">
                                                                                    <div className="p-2 rounded-3 text-primary d-inline-flex align-items-center justify-content-center" style={{ backgroundColor: isPublished ? '#dcfce7' : '#e0e7ff' }}>
                                                                                        <FaGraduationCap className={`fs-5 ${isPublished ? 'text-success' : 'text-primary'}`} />
                                                                                    </div>
                                                                                    <div>
                                                                                        <div className="d-flex align-items-center gap-2">
                                                                                            <h5 className="fw-bold text-dark mb-0 fs-16">
                                                                                                {tItem.name}
                                                                                            </h5>
                                                                                            {/* Green badge when is_publish === true */}
                                                                                            {isPublished ? (
                                                                                                <Badge bg="success" className="px-2.5 py-1 fs-11 fw-bold d-inline-flex align-items-center gap-1 rounded-pill" style={{ backgroundColor: '#10b981' }}>
                                                                                                    <FaCheckCircle className="fs-11 text-white" /> Published
                                                                                                </Badge>
                                                                                            ) : (
                                                                                                <Badge bg="warning" className="text-dark px-2.5 py-1 fs-11 fw-bold d-inline-flex align-items-center gap-1 rounded-pill" style={{ backgroundColor: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' }}>
                                                                                                    <FaClock className="fs-11 text-warning" /> Unpublished
                                                                                                </Badge>
                                                                                            )}
                                                                                        </div>
                                                                                        <span className="text-muted fs-12 d-inline-flex align-items-center gap-1 mt-1">
                                                                                            <FaCalendarAlt className="text-secondary fs-11" />
                                                                                            Test Date: {tItem.date ? moment(tItem.date).format("DD MMMM YYYY") : "N/A"}
                                                                                        </span>
                                                                                    </div>
                                                                                </div>
                                                                            </div>

                                                                            {/* Scorecard Hero Widget */}
                                                                            <div className="p-2.5 px-3 rounded-3 border bg-light d-flex align-items-center gap-3 shadow-2xs">
                                                                                {overallMarksObtained !== null ? (
                                                                                    <>
                                                                                        <div className="text-center border-end pe-3">
                                                                                            <span className="fs-10 text-uppercase fw-bold text-muted d-block">
                                                                                                Overall Score {isOverallCalculated && <span className="text-primary opacity-75">(Calc)</span>}
                                                                                            </span>
                                                                                            <span className={`fs-18 fw-extrabold ${isPublished ? 'text-success' : 'text-primary'}`}>
                                                                                                {overallMarksObtained} <span className="fs-12 text-muted fw-normal">/ {overallMaxMarks ?? 100}</span>
                                                                                            </span>
                                                                                        </div>
                                                                                        <div className="d-flex flex-column gap-1">
                                                                                            <span className={`badge px-2.5 py-1.5 fs-11 fw-bold d-inline-flex align-items-center gap-1 ${isOverallPassed ? 'bg-success text-white' : 'bg-danger text-white'
                                                                                                }`} style={{ borderRadius: '6px' }}>
                                                                                                {isOverallPassed ? <FaCheckCircle className="fs-11" /> : <FaTimesCircle className="fs-11" />}
                                                                                                {isOverallPassed ? 'PASSED' : 'FAILED'} {overallPercentage !== null && `(${overallPercentage}%)`}
                                                                                            </span>
                                                                                            {overallMinMarks !== null && (
                                                                                                <span className="fs-11 text-secondary" style={{ fontSize: '10.5px' }}>
                                                                                                    Min Required: <span className="fw-bold text-dark">{overallMinMarks} Marks</span>
                                                                                                </span>
                                                                                            )}
                                                                                        </div>
                                                                                    </>
                                                                                ) : (
                                                                                    <div className="d-flex align-items-center gap-2 py-1 px-2">
                                                                                        <FaClock className="text-warning fs-5" />
                                                                                        <div>
                                                                                            <span className="fw-bold text-dark fs-12 d-block">
                                                                                                {isPublished ? "Overall Result Pending" : "Result Not Published"}
                                                                                            </span>
                                                                                            <span className="text-muted fs-11">
                                                                                                {isPublished ? "Evaluation in progress" : "Result has not been published yet"}
                                                                                            </span>
                                                                                        </div>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </Card.Header>

                                                                    <Card.Body className="p-3 p-md-4">
                                                                        {/* Clickable Header to Collapse/Expand entire Test Breakdown (Default Closed) */}
                                                                        {(() => {
                                                                            const testKey = tItem.id || tIdx;
                                                                            const isTestOpen = openTests[testKey] === true; // Default false (collapsed on load)

                                                                            // Filter papers based on Matrix rules:
                                                                            // Condition 1: Chapter = Yes, Paper = Calculate -> Show Paper
                                                                            // Condition 2: Chapter = No, Paper = Yes -> Show Paper
                                                                            // Condition 3: Chapter = No, Paper = No -> No Show Paper
                                                                            const visiblePapers = papers.filter(pGroup => pGroup.chapters.length > 0 || pGroup.finalPaperMarks !== null);

                                                                            return (
                                                                                <>
                                                                                    <div
                                                                                        className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom user-select-none"
                                                                                        style={{ cursor: 'pointer' }}
                                                                                        onClick={() => toggleTestOpen(testKey)}
                                                                                    >
                                                                                        <h6 className="fw-bold text-dark mb-0 fs-12 text-uppercase text-muted d-flex align-items-center gap-2">
                                                                                            <FaBookOpen className="text-primary" /> Papers & Chapter Mark Details
                                                                                            <span className="badge bg-light text-secondary border px-2 py-0.5 fs-11 font-normal ms-1">
                                                                                                {visiblePapers.length} {visiblePapers.length === 1 ? 'Paper' : 'Papers'} Mapped
                                                                                            </span>
                                                                                        </h6>
                                                                                        <div className="d-flex align-items-center gap-1.5 text-primary fw-semibold fs-12">
                                                                                            <span>{isTestOpen ? 'Hide Details' : 'Show Details'}</span>
                                                                                            {isTestOpen ? <FaChevronUp className="fs-11" /> : <FaChevronDown className="fs-11" />}
                                                                                        </div>
                                                                                    </div>

                                                                                    <Collapse in={isTestOpen}>
                                                                                        <div>
                                                                                            {visiblePapers.length === 0 ? (
                                                                                                <div className="text-center py-3 bg-light rounded-3 border border-dashed">
                                                                                                    <p className="text-muted small italic mb-0">No paper or chapter breakdown details configured for this test.</p>
                                                                                                </div>
                                                                                            ) : (
                                                                                                <div className="d-flex flex-column gap-3">
                                                                                                    {visiblePapers.map((pGroup, pIdx) => {
                                                                                                        const paperKey = `${testKey}_${pGroup.paperId || pIdx}`;
                                                                                                        const isPaperOpen = openPapers[paperKey] === true; // Default false (collapsed on load)
                                                                                                        const hasChapters = pGroup.chapters.length > 0;

                                                                                                        return (
                                                                                                            <div
                                                                                                                key={pGroup.paperId || pIdx}
                                                                                                                className="border rounded-3 p-3 shadow-2xs transition-all"
                                                                                                                style={{ borderColor: '#e2e8f0', backgroundColor: '#fafafa' }}
                                                                                                            >
                                                                                                                {/* Paper Header Row (Clickable to Collapse/Expand Paper Chapters ONLY if chapters exist) */}
                                                                                                                <div
                                                                                                                    className="d-flex align-items-center justify-content-between flex-wrap gap-2 pb-2.5 mb-2.5 border-bottom user-select-none"
                                                                                                                    style={{ borderColor: '#e2e8f0', cursor: hasChapters ? 'pointer' : 'default' }}
                                                                                                                    onClick={() => hasChapters && togglePaperOpen(paperKey)}
                                                                                                                >
                                                                                                                    <div className="d-flex align-items-center gap-2">
                                                                                                                        <span className="badge bg-primary text-white p-1.5 rounded-2 d-inline-flex">
                                                                                                                            <FaFileAlt className="fs-12" />
                                                                                                                        </span>
                                                                                                                        <div>
                                                                                                                            <span className="fw-bold text-dark fs-14 text-capitalize">
                                                                                                                                Paper: {pGroup.paperName}
                                                                                                                            </span>
                                                                                                                            {pGroup.paperObj?.description && (
                                                                                                                                <span className="text-muted fs-11 d-block">{pGroup.paperObj.description}</span>
                                                                                                                            )}
                                                                                                                        </div>
                                                                                                                    </div>

                                                                                                                    {/* Type 2: Paper-Level Test Result & Collapse Chevron */}
                                                                                                                    <div className="d-flex align-items-center gap-2">
                                                                                                                        {pGroup.finalPaperMarks !== null ? (
                                                                                                                            <Badge bg="indigo" className="bg-indigo-subtle text-indigo border border-indigo-subtle px-3 py-1.5 fs-12 fw-bold" style={{ backgroundColor: '#e0e7ff', color: '#3730a3', border: '1px solid #c7d2fe' }}>
                                                                                                                                Paper Score: {pGroup.finalPaperMarks} Marks {pGroup.isPaperCalculated ? '(Calculated)' : 'Obtained'}
                                                                                                                            </Badge>
                                                                                                                        ) : (
                                                                                                                            <Badge bg="light" className="text-secondary border px-2.5 py-1 fs-11 fw-semibold">
                                                                                                                                Paper Result: Pending
                                                                                                                            </Badge>
                                                                                                                        )}

                                                                                                                        {/* Chevron icon ONLY shown when Chapter = Yes */}
                                                                                                                        {hasChapters && (
                                                                                                                            <span className="text-secondary fs-12 ms-1">
                                                                                                                                {isPaperOpen ? <FaChevronUp /> : <FaChevronDown />}
                                                                                                                            </span>
                                                                                                                        )}
                                                                                                                    </div>
                                                                                                                </div>

                                                                                                                {/* Type 3: Chapter-Level Test Results Breakdown (Chapter = Yes -> Show, Chapter = No -> No Show) */}
                                                                                                                {hasChapters && (
                                                                                                                    <Collapse in={isPaperOpen}>
                                                                                                                        <div>
                                                                                                                            <div className="d-flex flex-column gap-2 mt-2">
                                                                                                                                {pGroup.chapters.map((ch, cIdx) => {
                                                                                                                                    const isPassed = ch.marksObtained !== null && ch.minMarks !== null && ch.marksObtained >= ch.minMarks;
                                                                                                                                    const isFailed = ch.marksObtained !== null && ch.minMarks !== null && ch.marksObtained < ch.minMarks;
                                                                                                                                    const chPercentage = ch.marksObtained !== null && ch.maxMarks ? Math.round((ch.marksObtained / ch.maxMarks) * 100) : null;

                                                                                                                                    return (
                                                                                                                                        <div
                                                                                                                                            key={ch.mapId || ch.chapterId || cIdx}
                                                                                                                                            className="bg-white p-3 rounded-3 border d-flex align-items-center justify-content-between flex-wrap gap-2 transition-all chapter-item-row"
                                                                                                                                            style={{ borderColor: '#edf2f7' }}
                                                                                                                                        >
                                                                                                                                            {/* Left: Chapter Details */}
                                                                                                                                            <div className="d-flex align-items-center gap-2.5">
                                                                                                                                                <div className="p-2 rounded-circle bg-light text-secondary d-inline-flex">
                                                                                                                                                    <FaBookOpen className="fs-12" />
                                                                                                                                                </div>
                                                                                                                                                <div>
                                                                                                                                                    <h6 className="fw-bold text-dark mb-0 fs-13">
                                                                                                                                                        {capitalize(ch.chapterName)}
                                                                                                                                                    </h6>
                                                                                                                                                    {ch.chapterDescription ? (
                                                                                                                                                        <p className="text-muted fs-11 mb-0 mt-0.5">{ch.chapterDescription}</p>
                                                                                                                                                    ) : (
                                                                                                                                                        <span className="text-muted fs-11 italic">No description</span>
                                                                                                                                                    )}
                                                                                                                                                </div>
                                                                                                                                            </div>

                                                                                                                                            {/* Right: Marks Details & Obtained Status */}
                                                                                                                                            <div className="d-flex align-items-center flex-wrap gap-2 ms-auto">
                                                                                                                                                {/* Max & Min Marks Chips */}
                                                                                                                                                <div className="d-flex align-items-center gap-1 bg-light p-1 px-2 rounded-2 border">
                                                                                                                                                    <span className="fs-11 text-muted">Max: <strong className="text-dark">{ch.maxMarks ?? '-'}</strong></span>
                                                                                                                                                    <span className="text-muted fs-11 opacity-50">|</span>
                                                                                                                                                    <span className="fs-11 text-muted">Min: <strong className="text-danger">{ch.minMarks ?? '-'}</strong></span>
                                                                                                                                                </div>

                                                                                                                                                {/* Marks Obtained Pill */}
                                                                                                                                                {ch.marksObtained !== null ? (
                                                                                                                                                    <div className="d-flex align-items-center gap-1.5">
                                                                                                                                                        <span
                                                                                                                                                            className={`badge px-3 py-1.5 fs-12 fw-bold d-inline-flex align-items-center gap-1 ${isPassed
                                                                                                                                                                    ? 'bg-success-subtle text-success border border-success-subtle'
                                                                                                                                                                    : (isFailed ? 'bg-danger-subtle text-danger border border-danger-subtle' : 'bg-primary-subtle text-primary border border-primary-subtle')
                                                                                                                                                                }`}
                                                                                                                                                            style={{ borderRadius: '6px' }}
                                                                                                                                                        >
                                                                                                                                                            {isPassed && <FaCheckCircle className="fs-11 text-success" />}
                                                                                                                                                            {isFailed && <FaTimesCircle className="fs-11 text-danger" />}
                                                                                                                                                            Obtained: {ch.marksObtained}
                                                                                                                                                            {chPercentage !== null && <span className="fs-11 opacity-75">({chPercentage}%)</span>}
                                                                                                                                                        </span>
                                                                                                                                                    </div>
                                                                                                                                                ) : (
                                                                                                                                                    <span className="badge bg-warning-subtle text-warning border border-warning-subtle px-2.5 py-1.5 fs-11 fw-semibold" style={{ borderRadius: '6px' }}>
                                                                                                                                                        <FaClock className="me-1 fs-11" /> Pending
                                                                                                                                                    </span>
                                                                                                                                                )}
                                                                                                                                            </div>
                                                                                                                                        </div>
                                                                                                                                    );
                                                                                                                                })}
                                                                                                                            </div>
                                                                                                                        </div>
                                                                                                                    </Collapse>
                                                                                                                )}
                                                                                                            </div>
                                                                                                        );
                                                                                                    })}
                                                                                                </div>
                                                                                            )}
                                                                                        </div>
                                                                                    </Collapse>
                                                                                </>
                                                                            );
                                                                        })()}
                                                                    </Card.Body>
                                                </Card>
                                            );
                                        }))}
                                    </>
                                );
                            })()}

                                        {/* LOAD MORE BUTTON FOR CHAPTER RESULT */}
                                        {subjectTests.length > visibleResultCount && (
                                            <div className="text-center my-4 py-2">
                                                <Button
                                                    variant="primary"
                                                    className="rounded-pill px-4 py-2.5 fw-bold shadow-sm d-inline-flex align-items-center gap-2"
                                                    onClick={() => setVisibleResultCount((prev) => prev + RESULT_PAGE_SIZE)}
                                                >
                                                    <span>Load More Test Results ({subjectTests.length - visibleResultCount} remaining)</span>
                                                    <FaChevronDown className="fs-8" />
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </Container>

            {/* Custom Embedded Scoped Styles matching SchoolNotice.js Timeline */}
            <style>{`
                .timeline-container {
                    position: relative;
                    padding-left: 48px;
                }
                .timeline-line {
                    position: absolute;
                    left: 20px;
                    top: 15px;
                    bottom: 25px;
                    width: 3px;
                    background: linear-gradient(to bottom, #10b981 0%, #64748b 100%);
                    border-radius: 2px;
                }
                .timeline-item {
                    position: relative;
                }
                .timeline-dot {
                    position: absolute;
                    left: -48px;
                    top: 15px;
                    width: 36px;
                    height: 36px;
                    border: 4px solid #ffffff;
                    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.15);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: 700;
                    font-size: 13px;
                    z-index: 2;
                }
                .tab-simple-btn {
                    font-size: 0.95rem;
                    color: #64748b;
                }
                .tab-simple-active {
                    color: #4f46e5 !important;
                    border-bottom: 3px solid #4f46e5 !important;
                    background: transparent !important;
                }
                .spin {
                    animation: spin 1s infinite linear;
                }
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                .chapter-result-card {
                    transition: transform 0.2s ease, box-shadow 0.2s ease;
                }
                .chapter-result-card:hover {
                    box-shadow: 0 6px 20px rgba(0,0,0,0.06) !important;
                }
                .chapter-item-row {
                    transition: background-color 0.15s ease, border-color 0.15s ease;
                }
                .chapter-item-row:hover {
                    background-color: #f8fafc !important;
                    border-color: #cbd5e1 !important;
                }
                .shadow-2xs {
                    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.03);
                }
                .fw-extrabold {
                    font-weight: 800;
                }
                .fs-10 { font-size: 10px; }
                .fs-11 { font-size: 11px; }
                .fs-12 { font-size: 12px; }
                .fs-13 { font-size: 13px; }
                .fs-14 { font-size: 14px; }
                .fs-16 { font-size: 16px; }
                .fs-18 { font-size: 18px; }
                .fs-8 { font-size: 0.75rem; }
                .line-clamp-2 {
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                }
            `}</style>
        </div>
    );
};

export default ChapterTest;